import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { CalendarSheet } from '@/components/CalendarSheet';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import { Snackbar } from '@/components/Snackbar';
import { useStartBankPayment } from '@/pages/Subscription/hooks/useStartBankPayment';
import { formatDateWithWeekday, toApiDateTime } from '@/utils/date';
import { getRecurringEndDates } from '@/utils/subscription';
import {
  BorderRadius,
  ButtonVariant,
  PaymentMethod,
  Size,
  SnackbarVariant,
} from '@/types/components';
import type { PaymentOption } from '@/types/components';

interface Props {
  option: PaymentOption;
}

export const CardPayment: React.FC<Props> = ({ option }) => {
  const { t } = useTranslation();
  const { defaultDate, minDate, maxDate } = getRecurringEndDates(option);
  const [endDate, setEndDate] = useState<Date>(defaultDate);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);

  const { startBankPayment, pendingMethod } = useStartBankPayment({
    onError: () => setIsErrorSnackbarOpen(true),
  });

  const handlePick = (date: Date) => {
    setEndDate(date);
    setIsCalendarOpen(false);
  };

  return (
    <div className="flex h-full flex-col justify-between gap-8 rounded-2xl bg-card p-6 shadow-card">
      <div className="flex flex-col gap-4">
        <p className="text-[18px]/[24px] font-semibold text-foreground md:text-[20px]/[28px] md:font-medium">
          {t('subscription.checkout.cardTitle')}
        </p>
        <div className="flex flex-col gap-4">
          <p className="text-body-18 text-foreground">
            {t('subscription.checkout.cardEndDateTitle')}
          </p>
          <div className="flex flex-col gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsCalendarOpen((open) => !open)}
                className="flex h-14 w-full cursor-pointer items-center justify-between gap-2 rounded-xl bg-on-primary-surface pr-3 pl-4 text-left"
              >
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-caption-12 text-muted-foreground">
                    {t('subscription.checkout.cardEndDatePrefix')}
                  </span>
                  <span className="truncate text-callout-16 text-foreground">
                    {formatDateWithWeekday(toApiDateTime(endDate))}
                  </span>
                </span>
                <ChevronRight
                  strokeWidth="2"
                  className="size-4 shrink-0 text-muted-foreground"
                />
              </button>
              <CalendarSheet
                isOpen={isCalendarOpen}
                filledDate={endDate}
                minDate={minDate}
                maxDate={maxDate}
                popoverClassName="left-0 mt-2 w-full md:w-[360px]"
                onChange={handlePick}
                onClose={() => setIsCalendarOpen(false)}
              />
            </div>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-body-14 text-subtle-foreground">
              <li>{t('subscription.checkout.cardCancelNote')}</li>
            </ul>
          </div>
        </div>
      </div>
      <Button
        variant={ButtonVariant.Primary}
        size={Size.Large}
        radius={BorderRadius.Pill}
        isLoading={Boolean(pendingMethod)}
        className="w-full md:rounded-xl"
        onClick={() =>
          startBankPayment({
            paymentMethod: PaymentMethod.Card,
            paymentOptionType: option.type,
            recurringPaymentEndDate: endDate,
          })
        }
      >
        {t('subscription.checkout.payWithCard')}
      </Button>

      <Snackbar
        compact
        isOpen={isErrorSnackbarOpen}
        message={t('global.errorOccurred')}
        variant={SnackbarVariant.Error}
        onDismiss={() => setIsErrorSnackbarOpen(false)}
      />
    </div>
  );
};
